<template>
  <b-modal
    id="operational_procedure_form_modal"
    @show="onModalShow"
    @hide="$emit('close')"
    hide-header
    no-close-on-esc
    no-close-on-backdrop
    :modal-class="'rfm-fullscreen'"
    :dialog-class="'rfm-dialog'"
    :content-class="'rfm-content'"
    body-class="rfm-body"
  >
    <div class="d-flex align-items-center justify-content-between">
      <h3 class="h4 text-capitalize mb-0 font-weight-bold">
        {{ tf("operational_procedure_exercise.title", "Operational Procedure Exercise") }}
      </h3>
      <b-badge pill variant="light-primary">
        {{ tf("custom_forms.state", "Estado") }}:
        {{ labelForState(currentState.toLowerCase()) }}
      </b-badge>
    </div>
    <hr class="mt-2 mb-2" />

    <div class="form-container">
      <div v-if="!readyToRenderForm" class="text-center my-2">
        <b-spinner label="Loading" variant="primary" />
      </div>

      <div v-else class="form-container-inner">
        <div v-if="loading" class="rfm-loading-overlay">
          <b-spinner label="Loading" variant="primary" />
        </div>
        <div class="rfm-grid">
          <div class="header-grid mb-3">
            <div class="info-card card">
              <div class="info-row">
                <span class="info-label text-body"
                  >{{ tf("risk.reference_number", "Número de Referencia") }}:</span
                >
                <span class="info-value text-body">{{ referenceNumber }}</span>
              </div>
              <div class="info-row">
                <span class="info-label text-body"
                  >{{ tc("unit.title", 1, "Instalación") }}:</span
                >
                <span class="info-value text-body">{{ unitNameDisplay }}</span>
              </div>
              <div class="info-row">
                <span class="info-label text-body">REMS:</span>
                <span class="info-value text-body">{{ rems }}</span>
              </div>
              <div class="info-row">
                <span class="info-label text-body">SIRH:</span>
                <span class="info-value text-body">{{ sirh }}</span>
              </div>
              <div
                v-if="associatedUnitsDisplayList.length"
                class="info-row info-row-associated"
              >
                <span class="info-label text-body">
                  {{
                    tf("simulacrum.associated_units", "Instalaciones asociadas")
                  }}:
                </span>
                <ul class="associated-units-list mb-0 pl-0">
                  <li
                    v-for="(unitLabel, index) in associatedUnitsDisplayList"
                    :key="`${unitLabel}-${index}`"
                    class="associated-units-item text-body"
                  >
                    {{ unitLabel }}
                  </li>
                </ul>
              </div>
              <div v-if="templateNames" class="info-row">
                <span class="info-label text-body"
                  >{{ tf("risk.template.title", "Plantilla") }}:</span
                >
                <span class="info-value text-body">{{ templateNames }}</span>
              </div>
            </div>

            <div class="history-card card">
              <div
                class="history-header d-flex align-items-center justify-content-between"
              >
                <strong>{{ tf("custom_forms.history", "Historial") }}</strong>
                <small class="text-muted">
                  {{ sortedHistory.length }}
                  {{ tf("custom_forms.entries", "entradas") }}
                </small>
              </div>

              <div class="history-table" v-if="sortedHistory.length">
                <div
                  class="history-row history-row--head bg-transparent"
                  :style="historyGridStyle"
                >
                  <div class="col-state text-body">
                    {{ tf("custom_forms.state", "Estado") }}
                  </div>
                  <div class="col-user text-body">
                    {{ tf("custom_forms.user", "Usuario") }}
                  </div>
                  <div class="col-date text-body">
                    {{ tf("custom_forms.date", "Fecha") }}
                  </div>
                  <div v-if="hasHistoryComments" class="col-note text-body">
                    {{ tf("custom_forms.note", "Nota") }}
                  </div>
                </div>

                <div
                  v-for="s in visibleHistory"
                  :key="s.id"
                  class="history-row"
                  :style="historyGridStyle"
                >
                  <div class="col-state">
                    <b-badge pill :variant="badgeFor(s.name)" class="history-badge">
                      {{ labelForState(s.name) }}
                    </b-badge>
                  </div>
                  <div class="col-user">
                    <div class="history-user" :title="formatUser(s)">
                      <div class="user-name text-body">{{ getUserName(s) || "—" }}</div>
                      <div v-if="getUserEmail(s)" class="user-email text-body">
                        {{ getUserEmail(s) }}
                      </div>
                    </div>
                  </div>
                  <div class="col-date">
                    <small class="history-date text-body">{{
                      formatDate(s.created_at)
                    }}</small>
                  </div>
                  <div v-if="hasHistoryComments" class="col-note">
                    <small class="text-muted" :title="getStatusComment(s)">{{
                      getStatusComment(s) || "—"
                    }}</small>
                  </div>
                </div>
              </div>

              <div v-else class="text-muted small py-1">
                {{ tf("custom_forms.empty", "Sin movimientos") }}
              </div>

              <div v-if="sortedHistory.length > limitHistory" class="text-right mt-1">
                <b-button
                  variant="link"
                  size="sm"
                  class="p-0"
                  @click="showAllHistory = !showAllHistory"
                >
                  {{
                    showAllHistory
                      ? tf("custom_forms.show_less", "Ver menos")
                      : tf("custom_forms.show_all", "Ver todo el historial")
                  }}
                </b-button>
              </div>
            </div>

            <div v-if="toc.length" class="index-card card mt-2">
              <div class="index-header d-flex align-items-center justify-content-between">
                <h3 class="h5 text-capitalize mb-0 font-weight-bold">
                  {{ tf("risk.index", "Índice") }}
                </h3>
              </div>
              <ul class="toc-list">
                <li v-for="sec in toc" :key="sec.id">
                  <a
                    href="#"
                    class="toc-link text-body"
                    @click.prevent="scrollToAnchor(sec.id)"
                  >
                    {{ sec.label }}
                  </a>
                  <ul v-if="sec.children && sec.children.length" class="toc-sub">
                    <li v-for="sub in sec.children" :key="sub.id">
                      <a
                        href="#"
                        class="toc-link text-body"
                        @click.prevent="scrollToAnchor(sub.id)"
                      >
                        {{ sub.label }}
                      </a>
                    </li>
                  </ul>
                </li>
              </ul>
            </div>
          </div>

          <div class="content-grid">
            <div class="main-stack">
              <build-operational-procedure-form
                v-if="readyToRenderForm"
                :key="runnerKey"
                ref="runner"
                :simulacrum-id="operationalProcedureId"
                :template-snapshot="templateSnapshot"
                :template-source-ids="templateSourceIds"
                :current-state="currentState"
                :is-reviewer="canReviewOperationalProcedure"
                :can-edit="canEditForm"
                @submit="onAnswersSubmit"
                @submit-success="onAnswersSubmit"
                @submitError="onAnswersSubmitError"
                @toc="onToc"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

<template #modal-footer="{ close }">
  <div class="w-100 rfm-footer">
    <div class="d-flex flex-row align-items-stretch justify-content-between">
      <div class="d-flex align-items-center flex-wrap">

        <b-button
          v-if="showSaveButton"
          variant="primary"
          class="mr-2 mb-1"
          :disabled="loading"
          @click="onClickSave"
        >
          {{ tf('actions.save', 'Guardar') }}
        </b-button>

        <!-- REVIEWER: aprobar/rechazar -->
        <template v-if="showReviewActions && canReviewOperationalProcedure">
          <b-button
            variant="success"
            class="mr-1 mb-1"
            :disabled="loading"
            @click="updateOperationalProcedureStatus('approved')"
          >
            {{ $t('actions.approve') }}
          </b-button>
          <b-button
            variant="danger"
            class="mr-1 mb-1"
            :disabled="loading"
            @click="updateOperationalProcedureStatus('rejected')"
          >
            {{ $t('actions.reject') }}
          </b-button>
        </template>

        <!-- REASIGNAR DESPUÉS DE RECHAZO -->
        <template v-else-if="canReassignAfterRejected">
          <div class="reviewer-inline mr-2 mb-1">
            <v-select
              v-model="selectedReassign"
              :options="reassignableOptions"
              :reduce="(u) => u.value"
              :clearable="false"
              :disabled="loading"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :placeholder="tf('custom_forms.select_reassigner', 'Selecciona usuario para reasignar')"
              input-id="reassign-after-reject"
              class="select-opens-up"
            />
          </div>
          <b-button
            variant="warning"
            class="mr-1 mb-1"
            :disabled="loading || !selectedReassign"
            @click="updateOperationalProcedureStatus('reassigned', selectedReassign)"
          >
            {{ tf('custom_forms.reassign', 'Reasignar') }}
          </b-button>
        </template>

        <!-- PERMISO ESPECIAL: reasignar en in_progress/reassigned -->
        <template v-else-if="canSpecialReassign">
          <div class="reviewer-inline mr-2 mb-1">
            <v-select
              v-model="selectedReassign"
              :options="reassignableOptions"
              :reduce="(u) => u.value"
              :clearable="false"
              :disabled="loading"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :placeholder="tf('custom_forms.select_reassigner', 'Selecciona usuario para reasignar')"
              input-id="special-reassign"
              class="select-opens-up"
            />
          </div>
          <b-button
            variant="warning"
            class="mr-1 mb-1"
            :disabled="loading || !selectedReassign"
            @click="updateOperationalProcedureStatus('reassigned', selectedReassign)"
          >
            {{ tf('custom_forms.reassign', 'Reasignar') }}
          </b-button>
        </template>

        <!-- ─── ENVIAR A REVISIÓN (owner en in_progress/reassigned) ─── -->
        <template v-else-if="canSendToReview">
          <div class="reviewer-inline mr-2 mb-1">
            <v-select
              v-model="selectedReviewer"
              :options="userOptions"
              :reduce="(u) => u.value"
              :clearable="false"
              :disabled="loading"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :placeholder="tf('custom_forms.select_reviewer', 'Selecciona usuario revisor')"
              input-id="reviewer"
              class="select-opens-up"
            />
          </div>
          <b-button
            variant="warning"
            class="mr-1 mb-1"
            :disabled="loading || !selectedReviewer"
            @click="updateOperationalProcedureStatus('in_review', selectedReviewer)"
          >
            {{ tf('custom_forms.send_to_review', 'Enviar a revisión') }}
          </b-button>
        </template>
      </div>

      <div class="d-flex align-items-center">
        <b-button variant="danger" class="mb-0" :disabled="loading" @click="close()">
          {{ tf('custom_forms.close', 'Cerrar') }}
        </b-button>
      </div>
    </div>
  </div>
</template>
  </b-modal>
</template>

<script>
import { BModal, BSpinner, BButton, BBadge } from "bootstrap-vue";
import vSelect from "vue-select";
import axios from "@axios";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import BuildOperationalProcedureForm from "@/components/BuildOperationalProcedureForm";
import ability from "@/libs/acl/ability";

export default {
  name: "ShowOperationalProcedureFormModal",
  components: {
    BModal,
    BSpinner,
    BButton,
    BBadge,
    vSelect,
    BuildOperationalProcedureForm,
  },
  props: {
    operationalProcedureId: { type: [Number, String], required: true },
    templateSnapshot: { type: Array, default: () => [] },
    templateSourceIds: { type: Array, default: () => [] },
  },
  data() {
    return {
      loading: false,
      referenceNumber: "",
      unitNameDisplay: "",
      templateNames: "",
      currentState: "in_progress",
      readyToRenderForm: false,
      runnerKey: 0,
      didBootstrap: false,
      creating: false,
      statuses: [],
      showAllHistory: false,
      limitHistory: 6,
      selectedReviewer: null,
      userOptions: [],
      reassignableOptions: [],
      selectedReassign: null,
      toc: [],
      _pendingSaveResolve: null,
      lastStatusUserId: null,
      rems: "",
      sirh: "",
      currentUnitId: null,
      unitRelated: [],
    };
  },
  computed: {
    /**
     * Rules: in_progress/reassigned -> just owner can save and send to review
     * in_review -> just reviewer can edit and send to review again
     * approved/rejected/cancel -> dont show save button.
     */

    canSendToReview() {
      const st = String(this.currentState || "").toLowerCase();
      if (st !== "in_progress" && st !== "reassigned") return false;
      return this.isAssignedToCurrentAuthorState();
    },

    canSpecialReassign() {
      const st = String(this.currentState || "").toLowerCase();
      if (st !== "in_progress" && st !== "reassigned" && st !== "in_review" && st !== "scheduled") return false;
      return this.hasSpecialReasingForOperationalProcedure();
    },
    canEditForm() {
      const state = String(this.currentState || "").toLowerCase();

      if (
        ["approved", "rejected", "not_approved", "scheduled", "canceled"].includes(state)
      ) {
        return false;
      }

      if (state === "in_review") {
        return false;
      }

      if (state === "in_progress" || state === "reassigned") {
        return this.isAssignedToCurrentAuthorState();
      }

      return false;
    },

    canReviewOperationalProcedure() {
      const hasPerm =
        this.can("review", "OperationalProcedure") ||
        this.can("review_operational_procedure", "OperationalProcedure");
      if (!hasPerm) return false;

      const st = String(this.currentState || "").toLowerCase();
      if (st !== "in_review") return false;

      let assignedId = this.lastStatusUserId;
      let assignedEmail = null;
      const list = Array.isArray(this.sortedHistory) ? this.sortedHistory : [];
      const lastReview = list.find(
        (s) => String(s?.name || "").toLowerCase() === "in_review"
      );

      if (lastReview) {
        assignedId =
          assignedId ||
          lastReview?.id_user ||
          lastReview?.user_id ||
          lastReview?.reviewer_id ||
          lastReview?.id_reviewer ||
          lastReview?.user?.id ||
          lastReview?.userId ||
          null;
        assignedEmail = lastReview?.user?.email || lastReview?.email || null;
      }

      const currentUserId = this.getLocalUserId();
      const currentUserEmail = this.getLocalUserEmail && this.getLocalUserEmail();

      const idMatch =
        Number(assignedId) > 0 &&
        Number(currentUserId) > 0 &&
        Number(assignedId) === Number(currentUserId);

      const emailMatch =
        assignedEmail &&
        currentUserEmail &&
        String(assignedEmail).toLowerCase() === String(currentUserEmail).toLowerCase();

      return !!(idMatch || emailMatch);
    },

    showReviewActions() {
      return String(this.currentState || "").toLowerCase() === "in_review";
    },

    showReassignWhenDecided() {
      return false;
    },

    canReassignAfterRejected() {
      const st = String(this.currentState || "").toLowerCase();
      const hasPerm =
        this.can("review", "OperationalProcedure") ||
        this.can("review_operational_procedure", "OperationalProcedure");
      return st === "rejected" && hasPerm;
    },

    canShowReassign() {
      const st = String(this.currentState || "").toLowerCase();
      return st !== "approved" && st !== "rejected";
    },

    sortedHistory() {
      return [...this.statuses].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
    },
    visibleHistory() {
      return this.showAllHistory
        ? this.sortedHistory
        : this.sortedHistory.slice(0, this.limitHistory);
    },
    hasHistoryComments() {
      return this.statuses.some((s) => !!this.getStatusComment(s));
    },
    historyGridStyle() {
      const base = "max-content minmax(140px, 1fr) minmax(190px, max-content)";
      return {
        gridTemplateColumns: this.hasHistoryComments
          ? `${base} minmax(140px, 1fr)`
          : base,
      };
    },
    associatedUnitsDisplayList() {
      const sourceUnits = Array.isArray(this.unitRelated) ? this.unitRelated : [];
      const normalizedCurrentUnitId =
        Number.isFinite(Number(this.currentUnitId)) ? Number(this.currentUnitId) : null;
      const uniqueById = new Map();

      sourceUnits.forEach((unit) => {
        if (!unit || unit.id == null) return;
        const normalizedUnitId = Number(unit.id);
        if (normalizedCurrentUnitId != null && normalizedUnitId === normalizedCurrentUnitId) {
          return;
        }
        if (uniqueById.has(normalizedUnitId)) return;
        const unitName = unit.description != null && String(unit.description).trim() !== ""
          ? String(unit.description).trim()
          : `Unidad ${normalizedUnitId}`;
        const unitSirh = unit.sirh != null && String(unit.sirh).trim() !== ""
          ? String(unit.sirh).trim()
          : "No SIRH";
        uniqueById.set(normalizedUnitId, `${unitName} (${unitSirh})`);
      });

      return [...uniqueById.values()];
    },

    showSaveButton() {
      const state = String(this.currentState || "").toLowerCase();

      //final states not showing save button
      if (state === "approved" || state === "rejected" || state === "canceled") {
        return false;
      }

      //in_review just reviewer can viwe and save
      if (state === "in_review") {
        let assignedId = this.lastStatusUserId;
        let assignedEmail = null;

        const list = Array.isArray(this.sortedHistory) ? this.sortedHistory : [];
        const lastReview = list.find(
          (s) => String(s?.name || "").toLowerCase() === "in_review"
        );

        if (lastReview) {
          assignedId =
            assignedId ||
            lastReview?.id_user ||
            lastReview?.user_id ||
            lastReview?.reviewer_id ||
            lastReview?.id_reviewer ||
            lastReview?.user?.id ||
            lastReview?.userId ||
            null;

          assignedEmail = lastReview?.user?.email || lastReview?.email || null;
        }

        const currentUserId = this.getLocalUserId();
        const currentUserEmail = this.getLocalUserEmail && this.getLocalUserEmail();

        if (!assignedId && !assignedEmail) return false;
        if (!currentUserId && !currentUserEmail) return false;

        const idMatch =
          Number(assignedId) > 0 &&
          Number(currentUserId) > 0 &&
          Number(assignedId) === Number(currentUserId);

        const emailMatch =
          assignedEmail &&
          currentUserEmail &&
          String(assignedEmail).toLowerCase() === String(currentUserEmail).toLowerCase();

        return !!(idMatch || emailMatch);
      }

      // in_progress - reassigned checks owner and assigned
      if (state === "in_progress" || state === "reassigned") {
        return this.isAssignedToCurrentAuthorState();
      }

      //other states shows nothing
      return false;
    },
  },
  created() {
    this.loadReviewerOptions();
    this.loadReassignableOptions();
  },
  methods: {
    hasSpecialReasingForOperationalProcedure() {
      try {
        return !!(this.$can && this.$can("special_reasing", "OperationalProcedure"));
      } catch (_) {
        return false;
      }
    },
    can(action, subject) {
      try {
        return typeof this.$can === "function"
          ? this.$can(action, subject)
          : ability.can(action, subject);
      } catch (e) {
        return ability.can(action, subject);
      }
    },
    onToc(t) {
      this.toc = Array.isArray(t) ? t : [];
    },
    scrollToAnchor(id) {
      const safeId = window.CSS && CSS.escape ? CSS.escape(id) : id;
      const modalEl = document.querySelector(".modal.show");
      if (!modalEl) return;
      const container =
        modalEl.querySelector(".form-container") ||
        modalEl.querySelector(".modal-body") ||
        modalEl.querySelector(".modal-content") ||
        modalEl;
      const target =
        modalEl.querySelector(`.rfm-anchor.rfm-aid-${safeId}`) ||
        modalEl.querySelector(`#${safeId}`) ||
        null;
      if (!target) return;
      const cRect = container.getBoundingClientRect();
      const tRect = target.getBoundingClientRect();
      const top =
        tRect.top -
        cRect.top +
        container.scrollTop -
        container.clientHeight / 2 +
        tRect.height / 2;
      container.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });
    },
    buildUserOptionLabel(user) {
      const parts = [user.name, user.user, user.email].filter(Boolean);
      return parts.join(" | ");
    },

    async loadReviewerOptions() {
      const mapUsers = (arr) =>
        (arr || []).map((u) => ({
          value: u.id,
          label: this.buildUserOptionLabel(u),
        }));
      try {
        const { data } = await axios.get("/v1/operational_procedure-get-reviewers");
        const list = data?.data || data || [];
        if (Array.isArray(list) && list.length) {
          this.userOptions = mapUsers(list);
          return;
        }
      } catch (_) {
        /* */
      }
      try {
        const { data } = await axios.get("/v1/operational_procedure-get-reviewers");
        const list = data?.data || data || [];
        this.userOptions = mapUsers(list);
      } catch (_) {
        this.userOptions = [];
      }
    },
    async loadReassignableOptions() {
      try {
        const { data } = await axios.get("/v1/operational_procedure-get-reassignable");
        const list = data?.data || data || [];
        this.reassignableOptions = (list || []).map((u) => ({
          value: u.id,
          label: this.buildUserOptionLabel(u),
        }));
      } catch (_) {
        this.reassignableOptions = [];
      }
    },
    tf(key, fallback = "") {
      try {
        const out = this.$t ? this.$t(key) : key;
        return typeof out === "string" && out === key ? fallback : out;
      } catch {
        return fallback;
      }
    },
    tc(key, count = 1, fallback = "") {
      try {
        const out = this.$tc ? this.$tc(key, count) : key;
        return typeof out === "string" && out === key ? fallback : out;
      } catch {
        return fallback;
      }
    },
    humanize(str = "") {
      return String(str)
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
    },
    labelForState(name) {
      const raw = String(name || "");
      const norm = raw
        .trim()
        .toLowerCase()
        .replace(/[-\s]+/g, "_");
      const aliases = { canceled: "cancelled" };
      const canonical = aliases[norm] || norm;
      const tryKeys = [
        `procedure_states.${canonical}`,
        `procedure_states.value.${canonical}`,
      ];
      for (const k of tryKeys) {
        const v = this.$t ? this.$t(k) : k;
        if (typeof v === "string" && v !== k) return v;
      }
      return this.humanize(canonical);
    },
    async onModalShow() {
      this.readyToRenderForm = false;
      this.runnerKey += 1;
      // Ensure reviewer options are available when opening
      if (!Array.isArray(this.userOptions) || !this.userOptions.length) {
        await this.loadReviewerOptions();
      }
      await this.loadOperationalProcedure();
      this.readyToRenderForm = (await this.ensureAnswerExists()) !== false;
      this.$nextTick(() => {
        this.$refs.runner?.reloadForm?.();
      });
    },
    async loadOperationalProcedure() {
      if (!this.operationalProcedureId) return;
      try {
        this.loading = true;
        const { data } = await axios.get(
          `/v1/operational_procedure/${this.operationalProcedureId}`
        );
        const ev = data && data.data ? data.data : data;
        this.referenceNumber = ev.reference_number || "";
        this.unitNameDisplay = ev.unit?.description || "";
        this.currentUnitId = ev.unit?.id ?? null;
        this.unitRelated = Array.isArray(ev.unit_related) ? ev.unit_related : [];
        this.rems = ev.rems ?? ev.unit?.rems ?? "";
        this.sirh = ev.sirh ?? ev.unit?.sirh ?? "";
        this.currentState = ev.last_status?.name || "in_progress";
        this.statuses = Array.isArray(ev.statuses) ? ev.statuses : [];
        this.lastStatusUserId =
          ev.last_status?.id_user ||
          ev.last_status?.user_id ||
          ev.last_status?.reviewer_id ||
          ev.last_status?.id_reviewer ||
          ev.last_status?.user?.id ||
          null;
        if (String(this.currentState).toLowerCase() === "rejected") {
          this.loadReassignableOptions();
        } else {
          this.selectedReassign = null;
        }
        if (ev.formAnswer?.template_sources?.length) {
          this.templateNames = ev.formAnswer.template_sources
            .map((s) => (s && s.name ? s.name : null))
            .filter(Boolean)
            .join(", ");
        } else if (this.templateSourceIds?.length) {
          const srcObjs = await this.resolveTemplateSourcesByIds(this.templateSourceIds);
          this.templateNames = srcObjs.map((s) => s.name).join(", ");
        } else {
          this.templateNames = "";
        }
      } catch (e) {
        /* noop */
      } finally {
        this.loading = false;
      }
    },
    async resolveTemplateSourcesByIds(ids = []) {
      if (!Array.isArray(ids) || !ids.length) return [];
      try {
        const { data } = await axios.get("/v1/operational_procedure_templates");
        const all = data.data || [];
        const set = new Set(ids.map(Number));
        return all
          .filter((t) => set.has(Number(t.id)))
          .map((t) => ({ id: t.id, name: t.name }));
      } catch (_) {
        return [];
      }
    },
    async ensureAnswerExists() {
      if (this.didBootstrap || this.creating) return true;
      try {
        const resp = await axios.get(
          `/v1/oper_proc_form_answers/get-form-by-oper_proc/${this.operationalProcedureId}`
        );
        const fa = resp && resp.data ? resp.data.data || resp.data : null;

        // If API returns meta with the operational procedure, hydrate UI fields
        if (fa && fa.operational_procedure) {
          const op = fa.operational_procedure;
          if (op.reference_number) this.referenceNumber = op.reference_number;
          if (op.unit && op.unit.description) this.unitNameDisplay = op.unit.description;
          if (op.last_status && op.last_status.name)
            this.currentState = op.last_status.name;
          if (Array.isArray(op.statuses)) this.statuses = op.statuses.slice();
        }

        // If template sources are present, compute templateNames when not already set
        if (
          !this.templateNames &&
          fa &&
          Array.isArray(fa.template_sources) &&
          fa.template_sources.length
        ) {
          this.templateNames = fa.template_sources
            .map((s) => (s && s.name ? s.name : null))
            .filter(Boolean)
            .join(", ");
        }

        this.didBootstrap = true;
        return true;
      } catch (lookupError) {
        if (lookupError?.response?.status !== 404) {
          this.$toast?.error?.(
            lookupError?.response?.data?.message ||
              this.tf("custom_forms.cannot_load_form", "No se pudo cargar el formulario")
          );
          return false;
        }

        if (!this.templateSnapshot?.length && !this.templateSourceIds?.length) {
          if (this.isAssignedToCurrentAuthorState()) {
            this.$emit("need-form-selection", {
              id: this.operationalProcedureId,
              referenceNumber: this.referenceNumber,
              unitId: this.currentUnitId,
              unit: {
                id: this.currentUnitId,
                description: this.unitNameDisplay,
                rems: this.rems,
                sirh: this.sirh,
              },
              lastStatusName: this.currentState,
            });
            return false;
          }
          this.$toast?.error?.(
            this.tf(
              "custom_forms.must_use_wizard",
              "Este ejercicio aún no tiene formulario. Inícialo desde el asistente."
            )
          );
          return false;
        }

        this.creating = true;
        try {
          const formData = new FormData();
          formData.append("id_simulacrum", this.operationalProcedureId);
          formData.append("data", JSON.stringify({}));
          formData.append(
            "template_snapshot",
            JSON.stringify(this.templateSnapshot || [])
          );
          let sourcesToSend = [];
          if (this.templateSourceIds && this.templateSourceIds.length) {
            sourcesToSend = await this.resolveTemplateSourcesByIds(
              this.templateSourceIds
            );
          }
          formData.append("template_sources", JSON.stringify(sourcesToSend));
          await axios.post("/v1/oper_proc_form_answers", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          if (sourcesToSend.length) {
            this.templateNames = sourcesToSend.map((s) => s.name).join(", ");
          } else if (this.templateSnapshot?.length) {
            this.templateNames = this.tf("risk.template.composed", "Composed");
          }
          this.didBootstrap = true;
          await this.loadOperationalProcedure();
          return true;
        } catch (createError) {
          this.$toast?.error?.(
            createError?.response?.data?.message ||
              this.tf("custom_forms.cannot_start_form", "No se pudo iniciar el formulario")
          );
          return false;
        } finally {
          this.creating = false;
        }
      }
    },
    async updateOperationalProcedureStatus(newStatus, toUserId = null) {
      const guardedStatuses = ["approved", "rejected"];
      if (
        guardedStatuses.includes(newStatus) &&
        !this.can("review_operational_procedure", "OperationalProcedure")
      )
        return;
      if (!this.operationalProcedureId) return;

      if (newStatus === "in_review") {
        if (!toUserId) {
          if (this.$toast) {
            this.$toast.error(
              this.tf("custom_forms.select_reviewer", "Selecciona usuario revisor")
            );
          }
          return;
        }

        const canSend = await this.$refs.runner?.validateBeforeReview?.();
        if (!canSend || !canSend.ok) {
          const missing = canSend && canSend.missing ? canSend.missing : [];
          const missingCount = missing.length;
          if (missingCount > 0 && this.$toast) {
            const lastMissing = missing[missing.length - 1];
            const lastFieldName = lastMissing?.name || "";
            const message =
              missingCount === 1
                ? `${this.tf(
                    "simulacrum.one_required_field_missing",
                    "Falta 1 campo obligatorio"
                  )}: ${lastFieldName}`
                : `${this.tf(
                    "simulacrum.multiple_required_fields_missing",
                    `Faltan ${missingCount} campos obligatorios`
                  )}. Última: ${lastFieldName}`;
            this.$toast.error(message);
          }
          return;
        }
      }

      try {
        this.loading = true;
        const payload = { status: newStatus };
        if (toUserId) {
          payload.reviewer_id = toUserId;
          payload.id_reviewer = toUserId;
        }
        await axios.post(
          `/v1/operational_procedure/update_status/${this.operationalProcedureId}`,
          payload
        );
        this.currentState = newStatus;
        this.$toast?.success?.(
          `${this.tf("custom_forms.state", "Estado")}: ${this.labelForState(
            newStatus.toLowerCase()
          )}`
        );
        await this.loadOperationalProcedure();
        await this.ensureAnswerExists();
        this.$nextTick(() => this.$refs.runner?.reloadForm?.());
      } catch (err) {
        this.$toast?.error?.(
          this.tf("custom_forms.update_state_error", "No se pudo actualizar el estado")
        );
      } finally {
        this.loading = false;
      }
    },
    onAnswersSubmit() {
      if (typeof this._pendingSaveResolve === "function") {
        this._pendingSaveResolve({ success: true });
      }
    },
    onAnswersSubmitError() {
      if (typeof this._pendingSaveResolve === "function") {
        this._pendingSaveResolve({ success: false });
      }
      this.loading = false;
      this.$toast && this.$toast.error(this.tf("risk.save_failed", "No se pudo guardar"));
    },
    async onClickSave() {
      if (!this.operationalProcedureId) return;
      try {
        this.loading = true;
        const waitForSubmit = new Promise((resolve) => {
          this._pendingSaveResolve = resolve;
        });
        const safetyTimeoutMs = 120000;
        const safetyTimeout = new Promise((resolve) =>
          setTimeout(() => resolve(undefined), safetyTimeoutMs)
        );
        let triggered = false;
        if (this.$refs?.runner) {
          if (typeof this.$refs.runner.submitForm === "function") {
            this.$refs.runner.submitForm();
            triggered = true;
          } else if (this.$refs.runner.$refs?.form?.submit) {
            this.$refs.runner.$refs.form.submit();
            triggered = true;
          }
        }
        if (!triggered && typeof this._pendingSaveResolve === "function") {
          this._pendingSaveResolve({ success: false });
        }
        const runner = this.$refs?.runner || null;
        const waitRunner = runner
          ? this.waitForRunnerIdle(runner, safetyTimeoutMs)
          : Promise.resolve();
        const saveResult = await Promise.race([waitForSubmit, waitRunner, safetyTimeout]);

        if (saveResult && saveResult.success === true) {
          this.didBootstrap = false;
          await this.loadOperationalProcedure(false);
          await this.ensureAnswerExists();
          if (typeof this.$refs.runner?.reloadForm === "function") {
            await this.$refs.runner.reloadForm();
          }
          const msg = this.tf("custom_forms.answers_saved", "Respuestas guardadas");
          this.$toast &&
            this.$toast({
              component: ToastificationContent,
              props: { title: msg, icon: "CheckIcon", variant: "success" },
            });
        } else if (!(saveResult && saveResult.success === false)) {
          this.didBootstrap = false;
          await this.loadOperationalProcedure(false);
          await this.ensureAnswerExists();
          if (typeof this.$refs.runner?.reloadForm === "function") {
            await this.$refs.runner.reloadForm();
          }
        }
      } catch (err) {
        this._pendingSaveResolve = null;
        this.$toast &&
          this.$toast.error(this.tf("risk.save_failed", "No se pudo guardar"));
      } finally {
        this._pendingSaveResolve = null;
        this.loading = false;
      }
    },
    waitForRunnerIdle(runner, timeoutMs = 60000) {
      return new Promise((resolve) => {
        try {
          if (!runner || runner.loading === false) return resolve();
          let settled = false;
          const done = () => {
            if (!settled) {
              settled = true;
              if (typeof unwatch === "function") unwatch();
              clearTimeout(tid);
              resolve();
            }
          };
          const unwatch = runner.$watch("loading", (val) => {
            if (val === false) done();
          });
          const tid = setTimeout(done, Math.max(1000, Number(timeoutMs) || 60000));
        } catch (_) {
          resolve();
        }
      });
    },
    formatDate(iso) {
      if (!iso) return "";
      try {
        const d = new Date(iso);
        return d.toLocaleString();
      } catch {
        return iso;
      }
    },
    getUserName(s) {
      return s?.user_name || s?.user?.name || "";
    },
    getUserEmail(s) {
      const u = s?.user;
      if (!u) return "";
      if (typeof u === "string") return u;
      return u?.email || "";
    },
    formatUser(s) {
      const name = this.getUserName(s);
      const email = this.getUserEmail(s);
      return [name, email].filter(Boolean).join(" | ");
    },
    getStatusComment(s) {
      return s?.comment || s?.comments || s?.note || s?.notes || s?.description || "";
    },
    getLocalUserId() {
      try {
        const raw =
          localStorage.getItem("user") || localStorage.getItem("userData") || "";
        if (!raw) return null;
        const obj = JSON.parse(raw);
        const candidates = [
          obj?.id,
          obj?.user?.id,
          obj?.data?.id,
          obj?.user_id,
          obj?.uid,
        ];
        for (const c of candidates) {
          const n = Number(c);
          if (Number.isFinite(n) && n > 0) return n;
        }
        return null;
      } catch (_) {
        return null;
      }
    },
    getLocalUserEmail() {
      try {
        const raw =
          localStorage.getItem("user") || localStorage.getItem("userData") || "";
        if (!raw) return null;
        const obj = JSON.parse(raw);
        const candidates = [obj?.email, obj?.user?.email, obj?.data?.email];
        for (const c of candidates) {
          if (c && typeof c === "string" && c.includes("@")) return c;
        }
        return null;
      } catch (_) {
        return null;
      }
    },
    isAssignedToCurrentAuthorState() {
      const st = String(this.currentState || "").toLowerCase();
      if (!["in_progress", "reassigned"].includes(st)) return false;

      let assignedId = this.lastStatusUserId;
      let assignedEmail = null;
      const list = Array.isArray(this.sortedHistory) ? this.sortedHistory : [];

      const lastForState = list.find((s) => String(s?.name || "").toLowerCase() === st);

      console.log("lastForState OP", { lastForState });

      if (lastForState) {
        assignedId =
          assignedId ||
          lastForState?.id_user ||
          lastForState?.user_id ||
          lastForState?.owner_id ||
          lastForState?.id_owner ||
          lastForState?.reviewer_id ||
          lastForState?.id_reviewer ||
          lastForState?.user?.id ||
          lastForState?.userId ||
          null;

        assignedEmail = lastForState?.user?.email || lastForState?.email || null;
      }

      const currentUserId = this.getLocalUserId();
      const currentUserEmail = this.getLocalUserEmail && this.getLocalUserEmail();

      const idMatch =
        Number(assignedId) > 0 &&
        Number(currentUserId) > 0 &&
        Number(assignedId) === Number(currentUserId);

      const emailMatch =
        assignedEmail &&
        currentUserEmail &&
        String(assignedEmail).toLowerCase() === String(currentUserEmail).toLowerCase();

      return !!(idMatch || emailMatch);
    },
    badgeFor(name) {
      const map = {
        in_progress: "primary",
        in_review: "warning",
        approved: "success",
        rejected: "danger",
        scheduled: "info",
        canceled: "canceled",
        reassigned: "reassigned",
      };
      const key = String(name || "").toLowerCase();
      return map[key] || "secondary";
    },
  },
};
</script>

<style>
/* fullscreen modal shell */
.rfm-fullscreen {
  padding: 0 !important;
}
.rfm-dialog {
  width: 100vw;
  max-width: 100vw;
  height: 100vh;
  margin: 0;
}
.rfm-content {
  min-height: 100vh;
  height: 100vh;
  display: flex;
  flex-direction: column;
  border: 0;
  border-radius: 0;
}
.rfm-body {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}
</style>

<style scoped>
/* scrollable body area inside modal */
.form-container {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
}
.form-container-inner {
  position: relative;
  min-height: 100%;
}
.rfm-loading-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.85);
  z-index: 10;
}

/* --- DESKTOP: 2 columns (left: sidebar, right: form) --- */
.rfm-grid {
  display: grid;
  grid-template-columns: 1.1fr 1.9fr;
  grid-template-areas: "left right";
  gap: 12px;
  align-items: start;
}
.rfm-grid > .header-grid {
  grid-area: left;
  position: sticky;
  top: 0;
  align-self: start;
}
.rfm-grid > .content-grid {
  grid-area: right;
}

/* --- MOBILE: 1 column but reordered (form first, sidebar second) --- */
@media (max-width: 992px) {
  .rfm-grid {
    grid-template-columns: 1fr;
    grid-template-areas: "right" "left";
  }
  .rfm-grid > .header-grid {
    position: static;
  }
  .index-card {
    max-height: none;
  }
}

/* LEFT cards (sidebar stack) */
.header-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}
.info-card {
  background: #f9fafb;
  border: 1px solid #eceff3;
  border-radius: 8px;
  padding: 10px 12px;
}
.info-row {
  display: flex;
  gap: 6px;
  margin-bottom: 4px;
}
.info-row-associated {
  align-items: flex-start;
}
.info-label {
  font-weight: 600;
  color: #444;
}
.info-value {
  color: #111;
}
.associated-units-list {
  list-style: none;
}
.associated-units-item {
  line-height: 1.35;
}

.history-card {
  background: #fff;
  border: 1px solid #eceff3;
  border-radius: 8px;
  padding: 12px;
  overflow: hidden;
}
.history-header {
  margin-bottom: 8px;
}
.history-table {
  width: 100%;
}
.history-row {
  display: grid;
  grid-column-gap: 8px;
  column-gap: 8px;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px dashed #eee;
}
.history-row:last-child {
  border-bottom: 0;
}
.history-row--head {
  border-bottom: 1px solid #e9ecef;
  font-weight: 600;
  color: #444;
  padding-top: 0;
  padding-bottom: 8px;
}
.col-state {
  justify-self: start;
}
.col-user {
  justify-self: start;
  min-width: 0;
}
.col-date {
  justify-self: end;
  white-space: nowrap;
  overflow: visible;
}
.history-row--head > div,
.history-row > .col-user {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.col-note {
  white-space: normal;
  overflow: hidden;
}
.history-badge {
  display: inline-flex;
  align-items: center;
}
.history-date {
  font-size: 12px;
  color: #6c757d;
  font-variant-numeric: tabular-nums;
}

/* RIGHT content (form column) */
.content-grid {
  display: block;
}

/* footer & reviewer */
.rfm-footer {
  position: relative;
}
.reviewer-center {
  min-width: 0;
}
.reviewer-inline {
  flex: 0 1 560px;
  min-width: 460px;
  max-width: 680px;
}
@media (max-width: 768px) {
  .reviewer-inline {
    flex: 1 1 auto;
    min-width: 0;
  }
}
.reviewer-inline :deep(.vs__dropdown-toggle) {
  min-height: 38px;
}
.reviewer-inline :deep(.vs__dropdown-menu),
.reviewer-inline :deep(.vs__dropdown-option) {
  font-size: 0.8125rem;
}

/* TOC styles */
.index-card {
  background: #fff;
  border: 1px solid #eceff3;
  border-radius: 8px;
  padding: 12px;
  max-height: calc(100vh - 220px);
  overflow: auto;
}
.toc-list {
  list-style: none;
  margin: 0;
  padding-left: 0;
}
.toc-list > li {
  margin: 4px 0;
}
.toc-link {
  color: #374151;
  text-decoration: underline;
  text-underline-offset: 2px;
}
.toc-link:hover {
  color: #111827;
}
.toc-sub {
  list-style: none;
  margin: 4px 0 0 12px;
  padding-left: 0;
}
</style>

<!-- unscoped: dropdown layering and force open-up -->
<style>
.vs__dropdown-menu {
  z-index: 2055 !important;
}
.select-opens-up .vs__dropdown-menu {
  top: auto !important;
  bottom: 100% !important;
  margin-bottom: 4px;
}
</style>
